import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { usersAPI, authAPI, rolesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Layout from './Layout';

interface User {
  userId: number;
  firstName: string;
  lastName: string;
  login: string;
  roleId?: number;
  roleName?: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface RoleOption {
  roleId: number;
  roleName: string;
}

const addUserFormDefaults = {
  firstName: '',
  lastName: '',
  login: '',
  password: '',
  confirmPassword: '',
  roleId: 0 as number,
};

const editUserFormDefaults = {
  ...addUserFormDefaults,
  isActive: true,
};

const Users: React.FC = () => {
  const { user: authUser, logout } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roles, setRoles] = useState<RoleOption[]>([]);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFormData, setAddFormData] = useState(addUserFormDefaults);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSuccess, setAddSuccess] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState<typeof editUserFormDefaults & { isActive: boolean }>(editUserFormDefaults);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const isAdmin = authUser?.roleId === 1;
  const canEditUser = (user: User) => isAdmin || authUser?.userId === user.userId;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await rolesAPI.getAll();
        if (res.data?.status === 'success' && Array.isArray(res.data.data)) {
          setRoles(res.data.data);
        }
      } catch (e) {
      }
    };
    loadRoles();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(
        (user) =>
          user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.login.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const response = await usersAPI.getAll();
      const data = response.data?.data ?? [];
      setUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setAddDialogOpen(true);
    setAddError(null);
    setAddSuccess(false);
    setAddFormData({ ...addUserFormDefaults, roleId: roles[0]?.roleId ?? 0 });
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setAddError(null);
    setAddSuccess(false);
    setAddFormData(addUserFormDefaults);
  };

  const handleAddUser = async () => {
    setAddError(null);
    if (addFormData.password !== addFormData.confirmPassword) {
      setAddError('Passwords do not match');
      return;
    }
    if (addFormData.password.length < 6) {
      setAddError('Password must be at least 6 characters long');
      return;
    }
    if (!addFormData.roleId) {
      setAddError('Please select a role');
      return;
    }
    if (!addFormData.firstName?.trim() || !addFormData.lastName?.trim() || !addFormData.login?.trim()) {
      setAddError('Please fill in all required fields');
      return;
    }

    setIsAdding(true);
    try {
      await authAPI.register({
        firstName: addFormData.firstName.trim(),
        lastName: addFormData.lastName.trim(),
        login: addFormData.login.trim(),
        password: addFormData.password,
        roleId: addFormData.roleId,
      });
      setAddSuccess(true);
      await fetchUsers();
      setTimeout(() => {
        handleCloseAddDialog();
      }, 1500);
    } catch (error: any) {
      setAddError(error.response?.data?.message || 'Failed to add user');
    } finally {
      setIsAdding(false);
    }
  };

  const handleAddFormChange = (field: keyof typeof addUserFormDefaults, value: string | number) => {
    setAddFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      login: user.login,
      password: '',
      confirmPassword: '',
      roleId: user.roleId ?? roles[0]?.roleId ?? 0,
      isActive: user.isActive,
    });
    setEditDialogOpen(true);
    setEditError(null);
    setEditSuccess(false);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    setEditError(null);
    setEditSuccess(false);
    setEditFormData(editUserFormDefaults);
  };

  const handleEditFormChange = (field: keyof typeof editFormData, value: string | number | boolean) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    setEditError(null);
    if (editFormData.password !== editFormData.confirmPassword) {
      setEditError('Passwords do not match');
      return;
    }
    if (editFormData.password.length > 0 && editFormData.password.length < 6) {
      setEditError('Password must be at least 6 characters long');
      return;
    }
    if (!editFormData.firstName?.trim() || !editFormData.lastName?.trim() || !editFormData.login?.trim()) {
      setEditError('Please fill in all required fields');
      return;
    }
    setIsUpdating(true);
    try {
      const payload: Record<string, unknown> = {
        firstName: editFormData.firstName.trim(),
        lastName: editFormData.lastName.trim(),
        login: editFormData.login.trim(),
      };
      if (editFormData.password.length > 0) payload.password = editFormData.password;
      if (isAdmin) {
        payload.roleId = editFormData.roleId;
        payload.isActive = editFormData.isActive;
      }
      await usersAPI.update(editingUser.userId, payload);
      setEditSuccess(true);
      await fetchUsers();
      setTimeout(() => handleCloseEditDialog(), 1500);
    } catch (error: any) {
      setEditError(error.response?.data?.message || 'Failed to update user');
    } finally {
      setIsUpdating(false);
    }
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString();
  };

  const handleDeleteUser = (user: User) => {
    setDeletingUser(user);
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeletingUser(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser) return;
    const isSelf = authUser?.userId === deletingUser.userId;
    try {
      setIsDeleting(true);
      setDeleteError(null);
      await usersAPI.remove(deletingUser.userId);
      if (isSelf) {
        logout();
        window.location.href = '/login';
        return;
      }
      await fetchUsers();
      handleCloseDeleteDialog();
    } catch (error: any) {
      setDeleteError(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth={false} sx={{ mt: 2, mb: 4 }}>
        {/* Page Title */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h4" component="h1">
              Users Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{
                backgroundColor: '#CA4300',
                '&:hover': { backgroundColor: '#A03600' },
              }}
            >
              ADD USER
            </Button>
          </Box>
        </Paper>

        {/* Search + Showing x of x users */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              label="Search Users"
              placeholder="Search by name or login..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Showing {filteredUsers.length} of {users.length} users
            </Typography>
          </Box>
        </Paper>

        {/* Users Table */}
        <Paper>
          <TableContainer sx={{ overflowX: 'visible' }}>
            <Table size="small" sx={{ tableLayout: 'auto' }}>
              <TableHead>
                <TableRow>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Is Active</TableCell>
                  <TableCell>Last Login At</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Loading users...
                    </TableCell>
                  </TableRow>
                ) : filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow
                      key={user.userId}
                      hover
                      sx={{
                        '&:hover': { backgroundColor: 'rgba(202, 67, 0, 0.04)' },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {user.firstName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {user.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.roleName ?? '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.isActive ? 'Active' : 'Inactive'}
                          color={user.isActive ? 'success' : 'default'}
                          size="small"
                          sx={{
                            ...(user.isActive ? {} : { backgroundColor: '#e0e0e0', color: '#666' }),
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(user.lastLoginAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDateTime(user.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'nowrap' }}>
                          {canEditUser(user) && (
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditUser(user)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isAdmin && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteUser(user)}
                                sx={{ color: '#b91c1c' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Add User Modal (Sign Up fields) */}
        <Dialog open={addDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Add User</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {addError && <Alert severity="error">{addError}</Alert>}
              {addSuccess && <Alert severity="success">User added successfully.</Alert>}

              <TextField
                required
                fullWidth
                label="First Name"
                value={addFormData.firstName}
                onChange={(e) => handleAddFormChange('firstName', e.target.value)}
              />
              <TextField
                required
                fullWidth
                label="Last Name"
                value={addFormData.lastName}
                onChange={(e) => handleAddFormChange('lastName', e.target.value)}
              />
              <TextField
                required
                fullWidth
                label="Login"
                value={addFormData.login}
                onChange={(e) => handleAddFormChange('login', e.target.value)}
              />
              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={addFormData.roleId}
                  label="Role"
                  onChange={(e) => handleAddFormChange('roleId', Number(e.target.value))}
                >
                  {roles.map((r) => (
                    <MenuItem key={r.roleId} value={r.roleId}>
                      {r.roleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                required
                fullWidth
                label="Password"
                type="password"
                value={addFormData.password}
                onChange={(e) => handleAddFormChange('password', e.target.value)}
                helperText="At least 6 characters"
              />
              <TextField
                required
                fullWidth
                label="Confirm Password"
                type="password"
                value={addFormData.confirmPassword}
                onChange={(e) => handleAddFormChange('confirmPassword', e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseAddDialog}
              disabled={isAdding}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': {
                  borderColor: '#A03600',
                  backgroundColor: 'rgba(202, 67, 0, 0.08)',
                  boxShadow: 2,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddUser}
              variant="contained"
              disabled={isAdding}
              sx={{
                backgroundColor: '#CA4300',
                '&:hover': { backgroundColor: '#A03600' },
              }}
            >
              {isAdding ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {editError && <Alert severity="error">{editError}</Alert>}
              {editSuccess && <Alert severity="success">User updated successfully.</Alert>}

              <TextField
                required
                fullWidth
                label="First Name"
                value={editFormData.firstName}
                onChange={(e) => handleEditFormChange('firstName', e.target.value)}
              />
              <TextField
                required
                fullWidth
                label="Last Name"
                value={editFormData.lastName}
                onChange={(e) => handleEditFormChange('lastName', e.target.value)}
              />
              <TextField
                required
                fullWidth
                label="Login"
                value={editFormData.login}
                onChange={(e) => handleEditFormChange('login', e.target.value)}
              />
              <FormControl fullWidth required disabled={!isAdmin}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editFormData.roleId}
                  label="Role"
                  onChange={(e) => handleEditFormChange('roleId', Number(e.target.value))}
                >
                  {roles.map((r) => (
                    <MenuItem key={r.roleId} value={r.roleId}>
                      {r.roleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="New Password (leave blank to keep current)"
                type="password"
                value={editFormData.password}
                onChange={(e) => handleEditFormChange('password', e.target.value)}
                helperText="At least 6 characters if changing"
              />
              <TextField
                fullWidth
                label="Confirm New Password"
                type="password"
                value={editFormData.confirmPassword}
                onChange={(e) => handleEditFormChange('confirmPassword', e.target.value)}
              />
              {isAdmin && editingUser && authUser?.userId !== editingUser.userId && (
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editFormData.isActive ? 'active' : 'inactive'}
                    label="Status"
                    onChange={(e) => handleEditFormChange('isActive', e.target.value === 'active')}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseEditDialog}
              disabled={isUpdating}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': {
                  borderColor: '#A03600',
                  backgroundColor: 'rgba(202, 67, 0, 0.08)',
                  boxShadow: 2,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateUser}
              variant="contained"
              disabled={isUpdating}
              sx={{
                backgroundColor: '#CA4300',
                '&:hover': { backgroundColor: '#A03600' },
              }}
            >
              {isUpdating ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete User Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {deleteError && <Alert severity="error">{deleteError}</Alert>}
              <Typography variant="body1">
                Are you sure you want to delete the user "{deletingUser?.firstName} {deletingUser?.lastName}" ({deletingUser?.login})?
                {deletingUser && authUser?.userId === deletingUser.userId && (
                  <Typography component="span" display="block" color="error" sx={{ mt: 1 }}>
                    You are deleting your own account. You will be logged out.
                  </Typography>
                )}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={handleCloseDeleteDialog}
              disabled={isDeleting}
              sx={{
                borderColor: '#CA4300',
                color: '#CA4300',
                borderRadius: 1,
                boxShadow: 2,
                '&:hover': {
                  borderColor: '#A03600',
                  backgroundColor: 'rgba(202, 67, 0, 0.08)',
                  boxShadow: 2,
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              disabled={isDeleting}
              sx={{ backgroundColor: '#b91c1c', '&:hover': { backgroundColor: '#991b1b' } }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export { Users };
export default Users;
